import { useState, useMemo } from "react";
import { X, Building } from "lucide-react";
import { TransactionHistoryService } from "../../services/api/transactionHistory.service";
import toast from "react-hot-toast";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

interface LogDepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clinicId?: string;
}

export default function LogDepositModal({
  isOpen,
  onClose,
  onSuccess,
  clinicId,
}: LogDepositModalProps) {
  const now = useMemo(() => new Date(), []);
  const [amount, setAmount] = useState("");
  const [depositDate, setDepositDate] = useState(now.toISOString().split("T")[0]);
  const [depositMonth, setDepositMonth] = useState(MONTH_NAMES[now.getMonth()]);
  const [depositYear, setDepositYear] = useState(String(now.getFullYear()));
  const [paymentMode, setPaymentMode] = useState<"Cash" | "Cheque" | "Online">("Cash");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const yearOptions = useMemo(() => {
    const cur = new Date().getFullYear();
    return [cur - 2, cur - 1, cur, cur + 1];
  }, []);

  const handleSubmit = async () => {
    if (!amount || !depositDate || !depositMonth || !depositYear) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await TransactionHistoryService.createBankDeposit({
        clinicId,
        amount: parseFloat(amount),
        depositDate,
        depositMonth,
        depositYear,
        description: description || undefined,
      });
      if (res.success) {
        toast.success("Deposit logged successfully");
        onSuccess();
        resetForm();
        onClose();
      } else {
        toast.error(res.message || "Failed to log deposit");
      }
    } catch {
      toast.error("Failed to log deposit");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setAmount("");
    setDepositDate(new Date().toISOString().split("T")[0]);
    const n = new Date();
    setDepositMonth(MONTH_NAMES[n.getMonth()]);
    setDepositYear(String(n.getFullYear()));
    setPaymentMode("Cash");
    setDescription("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 animate-fadeIn">
      <div className="bg-white rounded-xl shadow-xl w-[440px] relative animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Building className="w-5 h-5 text-indigo-600" />
            <h3 className="text-base font-semibold text-gray-800">Log Deposit</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Amount + Deposit Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Deposit Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={depositDate}
                onChange={(e) => setDepositDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50"
              />
            </div>
          </div>

          {/* Deposit Month + Year */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Deposit Month <span className="text-red-500">*</span>
              </label>
              <select
                value={depositMonth}
                onChange={(e) => setDepositMonth(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50 bg-white"
              >
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Deposit Year <span className="text-red-500">*</span>
              </label>
              <select
                value={depositYear}
                onChange={(e) => setDepositYear(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50 bg-white"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Payment Mode</label>
            <div className="flex gap-2">
              {(["Cash", "Cheque", "Online"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setPaymentMode(mode)}
                  className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors cursor-pointer
                    ${paymentMode === mode
                      ? "border-secondary bg-red-50 text-secondary"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Note <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              placeholder="Eg: Parlor daily receipts..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-xs font-medium text-white bg-secondary rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 cursor-pointer"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

